import React, { memo } from 'react';
import { StyleSheet } from 'react-native';

import { sportSupportsScoreboard } from '@/components/scoreboard/use-scoreboard';
import {
    PickemScoreBoardAccordion,
    PickemScoreBoardWithSubscription,
} from '@/feature/entries-pickem/components/PickemScoreBoardAccordion';
import { PICKS_USE_EVENT_STALE_TIME, useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { useEventSupportsScoreboard } from '@/hooks/use-event-supports-scoreboard';
import { useGameTrackerConfig } from '@/hooks/use-game-tracker-config';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useEventDetail, useEventStatus } from '@/hooks/use-sbk-event-details-cache';

import { BasicEventHeader, BorderTopWrapper, EntrySectionEventHeaderProps } from './BasicEventHeader';

/**
 * This component renders the header for the event in the entry section
 * it can be the basic header or the one with the Scoreboard
 */
export const EntrySectionEventHeader = memo(
    ({ event: pickemEvent, status, showGameTrackerTooltip, onViewFullEvent }: EntrySectionEventHeaderProps) => {
        const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;
        const showScoreboard = useEventSupportsScoreboard(pickemEvent);
        const scoreboardEnabled = useJurisdictionStore(
            state => state.jurisdictionSettings?.productConfig?.settings?.scoreboard_enabled
        );

        const {
            data: event,
            refetch,
            error,
        } = useEvent(pamEventId ?? '', { staleTime: PICKS_USE_EVENT_STALE_TIME, enabled: !!pamEventId });
        const eventDetails = useEventDetail(event?.id ?? '') ?? event?.event_details;
        const hasScoreData = eventDetails?.period_scores?.length || eventDetails?.sport_specific?.inning_details;
        const hasEventData = event && !error && hasScoreData;

        const gameTrackerConfig = useGameTrackerConfig(event?.league, event?.event_details);
        const eventStatus = useEventStatus(event?.id ?? '') ?? event?.status;

        const eventSportSupportsScoreboard = event ? sportSupportsScoreboard(event.sport) : false;

        const canDisplayScoreboard =
            !!pickemEvent && !!showScoreboard && scoreboardEnabled && hasEventData && eventSportSupportsScoreboard;

        if (!pickemEvent) {
            return null;
        }

        if (!canDisplayScoreboard || !event) {
            return (
                <BasicEventHeader
                    event={pickemEvent}
                    status={status}
                    gameTrackerConfig={gameTrackerConfig}
                    showGameTrackerTooltip={showGameTrackerTooltip}
                    onViewFullEvent={onViewFullEvent}
                />
            );
        }

        if (eventStatus === 'LIVE') {
            return (
                <BorderTopWrapper style={styles.topWrapper}>
                    <PickemScoreBoardWithSubscription
                        eventInfo={pickemEvent}
                        event={event}
                        refetch={refetch}
                        showGameTrackerTooltip={showGameTrackerTooltip}
                        onViewFullEvent={onViewFullEvent}
                    />
                </BorderTopWrapper>
            );
        }

        return (
            <BorderTopWrapper style={styles.topWrapper}>
                <PickemScoreBoardAccordion
                    eventInfo={pickemEvent}
                    event={event}
                    showGameTrackerTooltip={showGameTrackerTooltip}
                    onViewFullEvent={onViewFullEvent}
                />
            </BorderTopWrapper>
        );
    }
);

const styles = StyleSheet.create({
    topWrapper: {
        paddingVertical: 5,
    },
});
